import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../shared/ui/components/dialog";
import { Button } from "../../shared/ui/components/button";
import { Input } from "../../shared/ui/components/input";
import { Switch, SwitchWrapper } from "../../shared/ui/components/switch";
import { Label } from "../../shared/ui/components/label";
import {
  DocumentModalProps,
  DocumentFormData,
  AddDocumentRequest,
  EditDocumentRequest
} from "../types";
import { Upload, FileText, File, X, AlertCircle } from "lucide-react";
import { useFileUpload } from "../hooks/useFileUpload";
import { formatFileSize } from "../utils/fileUtils";
import { UI_TEXT, MODAL_MODES } from "../constants";

const DocumentModal: React.FC<DocumentModalProps> = ({
  isOpen,
  onClose,
  document,
  projectId,
  onSave,
  mode,
  saveError
}) => {
  const [formData, setFormData] = useState<DocumentFormData>({
    filename: "",
    comments: "",
    isPublic: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    file,
    isDragOver,
    error: fileError,
    handleFileSelect,
    handleFileRemove,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    clearError
  } = useFileUpload({
    onFileSelect: (selectedFile) => {
      if (!formData.filename) {
        setFormData((prev) => ({ ...prev, filename: selectedFile.name }));
      }
    }
  });

  useEffect(() => {
    if (document && mode === MODAL_MODES.EDIT) {
      setFormData({
        filename: document.filename,
        comments: document.comments || "",
        isPublic: document.isPublic
      });
    } else {
      setFormData({
        filename: "",
        comments: "",
        isPublic: false
      });
      handleFileRemove();
    }
    setFormErrors({});
    clearError();
  }, [document, mode, isOpen, handleFileRemove, clearError]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.filename.trim()) {
      newErrors.filename = "Filename is required";
    }

    if (mode === MODAL_MODES.CREATE && !file) {
      newErrors.file = "Please select a file to upload";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !fileError;
  };

  const handleClearForm = () => {
    // Clear the file selection
    handleFileRemove();

    // Reset form data
    setFormData({
      filename: "",
      comments: "",
      isPublic: false
    });

    // Clear form errors
    setFormErrors({});

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (mode === MODAL_MODES.CREATE && file) {
        const addRequest: AddDocumentRequest = {
          projectId: projectId,
          file: file,
          filename: formData.filename,
          isPublic: formData.isPublic,
          comments: formData.comments || undefined
        };
        const result = await onSave(addRequest);

        // If save was successful, the parent will close the modal
        // If it failed, the modal stays open and shows the error
        // No need to clear file here - let user decide to change or cancel
      } else if (mode === MODAL_MODES.EDIT && document) {
        const editRequest: EditDocumentRequest = {
          documentId: document.documentId,
          projectId: projectId,
          filename: formData.filename,
          isPublic: formData.isPublic,
          comments: formData.comments || undefined
        };
        await onSave(editRequest);
      }
    } catch (error) {
      console.error("Error saving document:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[500px]"
        aria-describedby="document-dialog-description"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === MODAL_MODES.CREATE
              ? UI_TEXT.MODAL_TITLES.ADD
              : UI_TEXT.MODAL_TITLES.EDIT}
          </DialogTitle>
          <DialogDescription id="document-dialog-description">
            {mode === MODAL_MODES.CREATE
              ? "Upload a new document to the project"
              : "Edit document metadata and settings"}
          </DialogDescription>
        </DialogHeader>

        {saveError && (
          <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-500 rounded-md p-4 mb-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                  {saveError.includes("already exists") ||
                  saveError.includes("Duplicate")
                    ? "Duplicate File Detected"
                    : "Upload Error"}
                </h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                  {saveError}
                </p>
                {(saveError.includes("already exists") ||
                  saveError.includes("Duplicate")) && (
                  <>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">
                      <strong>Tip:</strong> You can upload a different file or
                      try again later.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleClearForm}
                      className="border-yellow-600 text-yellow-900 hover:bg-yellow-100 dark:border-yellow-400 dark:text-yellow-100 dark:hover:bg-yellow-900"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear and try again
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4 py-4">
          {mode === MODAL_MODES.EDIT && document && (
            <div className="bg-muted p-3 rounded-md">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>Original filename: {document.filename}</span>
              </div>
            </div>
          )}

          {mode === MODAL_MODES.CREATE && (
            <div className="space-y-4">
              {!file ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragOver
                      ? "border-primary bg-primary/10"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-4">
                    <div className="mx-auto w-12 h-12 text-gray-400">
                      <Upload className="w-full h-full" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-lg font-medium">
                        {isDragOver
                          ? UI_TEXT.DRAG_DROP.HOVER
                          : UI_TEXT.DRAG_DROP.DEFAULT}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {UI_TEXT.DRAG_DROP.BROWSE}
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) {
                          handleFileSelect(selectedFile);
                        }
                      }}
                      accept="*/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {UI_TEXT.DRAG_DROP.BUTTON}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <File className="h-8 w-8 text-primary" />
                      <div>
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleFileRemove}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {(formErrors.file || fileError) && (
                <p className="text-sm text-destructive">
                  {formErrors.file || fileError}
                </p>
              )}
            </div>
          )}

          {(mode === MODAL_MODES.EDIT ||
            (mode === MODAL_MODES.CREATE && file)) && (
            <>
              <div className="space-y-2">
                <Label htmlFor="filename">
                  {UI_TEXT.FORM_LABELS.FILENAME} *
                </Label>
                <Input
                  id="filename"
                  value={formData.filename}
                  onChange={(e) =>
                    setFormData({ ...formData, filename: e.target.value })
                  }
                  placeholder={UI_TEXT.PLACEHOLDERS.FILENAME}
                />
                {formErrors.filename && (
                  <p className="text-sm text-destructive">
                    {formErrors.filename}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="comments">{UI_TEXT.FORM_LABELS.COMMENTS}</Label>
                <textarea
                  id="comments"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.comments}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData({ ...formData, comments: e.target.value })
                  }
                  placeholder={UI_TEXT.PLACEHOLDERS.COMMENTS}
                  rows={3}
                />
              </div>

              <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg bg-muted/30">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="public-status" className="font-semibold">
                    {UI_TEXT.FORM_LABELS.PUBLIC_STATUS}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.isPublic
                      ? UI_TEXT.FORM_LABELS.PUBLIC_DESCRIPTION
                      : UI_TEXT.FORM_LABELS.PRIVATE_DESCRIPTION}
                  </p>
                </div>
                <div className="flex items-center pt-1 flex-shrink-0">
                  <SwitchWrapper>
                    <Switch
                      id="public-status"
                      checked={formData.isPublic}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, isPublic: checked })
                      }
                    />
                  </SwitchWrapper>
                </div>
              </div>
            </>
          )}

          {mode === MODAL_MODES.CREATE && !file && (
            <div className="text-center py-4">
              <p className="text-muted-foreground">
                {UI_TEXT.PLACEHOLDERS.SELECT_FILE}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {UI_TEXT.BUTTONS.CANCEL}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSaving || (mode === MODAL_MODES.CREATE && !file)}
          >
            {isSaving ? (
              UI_TEXT.BUTTONS.SAVING
            ) : mode === MODAL_MODES.CREATE ? (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {UI_TEXT.BUTTONS.UPLOAD}
              </>
            ) : (
              UI_TEXT.BUTTONS.UPDATE
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentModal;
